import React, { useEffect, useRef, useState } from "react";
import "src/containers/QuestionnaireParent/QuestionOptionsLibrary/components/DatePicker/DatePicker.scss";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import TextField from "@mui/material/TextField";
import moment from "moment";

import {
  convertYearToUTC,
  convertDateToUTC,
} from "src/latest/src/utils/timeZone";

export type DatePickerProps = {
  title?: string;
  placeholder?: string;
  className?: string;

  inputValue: string;
  onChange: (value: string) => void;

  mode?: "date" | "year";
  minDate?: Date;
  maxDate?: Date;

  disabled?: boolean;
  required?: boolean;

  requiredMessage?: string;
  invalidMessage?: string;
  onErrorChange?: (hasError: boolean) => void;
};

const MIN_YEAR = 1900;

function DatePicker({
  title,
  placeholder = "MM/DD/YYYY",
  inputValue,
  onChange,
  mode = "date",
  minDate,
  maxDate,
  disabled = false,
  required = false,
  requiredMessage = "This field is required",
  invalidMessage = "Invalid date",
  className = "",
  onErrorChange,
}: DatePickerProps): React.ReactElement {
  const ref = useRef<any>(null);

  const [value, setValue] = useState<Date | null>(null);
  const [touched, setTouched] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!inputValue) {
      setValue(null);
      setErrorMessage("");

      return;
    }

    try {
      const parsed =
        mode === "year"
          ? convertYearToUTC(inputValue)
          : convertDateToUTC(inputValue);

      if (!isNaN(parsed.getTime())) {
        setValue(parsed);
        setErrorMessage("");
      }
    } catch {
    }
  }, [inputValue, mode]);

  useEffect(() => {
    onErrorChange?.(Boolean(errorMessage));
  }, [errorMessage, onErrorChange]);

  const handleChange = (newValue: Date | null) => {
    setTouched(true);

    if (!newValue) {
      setValue(null);
      onChange("");
      setErrorMessage(required ? requiredMessage : "");

      return;
    }

    if (isNaN(newValue.getTime())) {
      setValue(newValue);
      setErrorMessage(invalidMessage);

      return;
    }

    const year = newValue.getFullYear();

    if (year < MIN_YEAR) {
      setErrorMessage(invalidMessage);

      return;
    }

    if (maxDate && year > maxDate.getFullYear()) {
      setErrorMessage(invalidMessage);

      return;
    }

    if (mode === "date") {
      if (
        (minDate && newValue < minDate) ||
        (maxDate && newValue > maxDate)
      ) {
        setErrorMessage(invalidMessage);

        return;
      }
    }

    const utcDate =
      mode === "year"
        ? convertYearToUTC(moment(newValue).format("YYYY"))
        : convertDateToUTC(moment(newValue).format("MM/DD/YYYY"));

    setValue(newValue);

    onChange(
      mode === "year"
        ? moment(utcDate).format("YYYY")
        : moment(utcDate).format("MM/DD/YYYY")
    );

    setErrorMessage("");
  };

  const displayPlaceholder = mode === "year" ? "YYYY" : placeholder;
  const showError = Boolean(touched && errorMessage);

  return (
    <div className={`date-container ${className}`}>
      {title && <span className="date-title">{title}</span>}

      <div className={showError ? "is-invalid date-picker" : "date-picker"}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DesktopDatePicker
            ref={ref}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            minDate={minDate ?? new Date(1900, 0, 1)}
            maxDate={maxDate ?? new Date()}
            views={mode === "year" ? ["year"] : ["year", "month", "day"]}
            reduceAnimations={true}
            slots={{ textField: TextField }}
            slotProps={{
              textField: {
                placeholder: displayPlaceholder,
                autoComplete: "off",
                error: showError,
              },
            }}
          />
        </LocalizationProvider>
      </div>

      {showError && (
        <div className="invalid-feedback mt-1">{errorMessage}</div>
      )}
    </div>
  );
}

export default DatePicker;
